<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useAlert } from 'dashboard/composables';
import WhatsappTemplatesAPI from 'dashboard/api/channel/whatsappTemplates';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';

const props = defineProps({
    inbox: { type: Object, required: true },
});

// State
const templates = ref([]);
const loading = ref(false);
const syncing = ref(false);
const searchQuery = ref('');
const filterCategory = ref('');
const filterStatus = ref('');

// Wizard
const showWizard = ref(false);
const wizardStep = ref(1);
const submitting = ref(false);

const form = ref({
    name: '', language: 'pt_BR', category: 'MARKETING',
    header: { type: '', text: '', example: '' },
    body: '', body_examples: [], footer: '', buttons: [],
});

function sanitizeTemplateName(value) {
    return value
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove accents
        .toLowerCase()
        .replace(/\s+/g, '_') // spaces → underscores
        .replace(/[^a-z0-9_]/g, ''); // strip anything else
}

watch(() => form.value.name, (newVal) => {
    const sanitized = sanitizeTemplateName(newVal);
    if (sanitized !== newVal) {
        form.value.name = sanitized;
    }
});

const CATEGORIES = [
    { key: 'MARKETING', label: 'Marketing', icon: 'i-lucide-megaphone', color: 'text-orange-500', bg: 'bg-orange-50', desc: 'Promoções, ofertas, cupons e boletins informativos.' },
    { key: 'UTILITY', label: 'Utilidade', icon: 'i-lucide-package', color: 'text-blue-500', bg: 'bg-blue-50', desc: 'Atualizações de pedidos, lembretes e confirmações.' },
    { key: 'AUTHENTICATION', label: 'Autenticação', icon: 'i-lucide-shield-check', color: 'text-green-500', bg: 'bg-green-50', desc: 'Códigos de verificação e senhas temporárias.' },
];

const LANGUAGES = [
    { key: 'pt_BR', label: 'Portuguese (BR)' },
    { key: 'en_US', label: 'English (US)' },
    { key: 'es', label: 'Spanish' },
];

const BUTTON_TYPES = [
    { key: 'QUICK_REPLY', label: 'Personalizado', icon: 'i-lucide-message-circle' },
    { key: 'URL', label: 'Acessar o site', icon: 'i-lucide-external-link' },
    { key: 'PHONE_NUMBER', label: 'Ligar', icon: 'i-lucide-phone' },
    { key: 'COPY_CODE', label: 'Copiar código da oferta', icon: 'i-lucide-copy' },
];

const MEDIA_TYPES = [
    { key: '', label: 'Nenhum', icon: 'i-lucide-circle-off' },
    { key: 'IMAGE', label: 'Imagem', icon: 'i-lucide-image' },
    { key: 'VIDEO', label: 'Vídeo', icon: 'i-lucide-video' },
    { key: 'DOCUMENT', label: 'Documento', icon: 'i-lucide-file-text' },
];

// FilterSelect option arrays
const categoryFilterOptions = [
    { label: 'Todas categorias', value: '', icon: 'i-lucide-layers' },
    { label: 'Marketing', value: 'MARKETING', icon: 'i-lucide-megaphone' },
    { label: 'Utilidade', value: 'UTILITY', icon: 'i-lucide-package' },
    { label: 'Autenticação', value: 'AUTHENTICATION', icon: 'i-lucide-shield-check' },
];
const statusFilterOptions = [
    { label: 'Todos status', value: '', icon: 'i-lucide-circle' },
    { label: 'Aprovado', value: 'APPROVED', icon: 'i-lucide-check-circle' },
    { label: 'Pendente', value: 'PENDING', icon: 'i-lucide-clock' },
    { label: 'Rejeitado', value: 'REJECTED', icon: 'i-lucide-x-circle' },
];
const languageOptions = [
    { label: 'Portuguese (BR)', value: 'pt_BR' },
    { label: 'English (US)', value: 'en_US' },
    { label: 'Spanish', value: 'es' },
];
const mediaTypeOptions = [
    { label: 'Nenhum', value: '', icon: 'i-lucide-circle-off' },
    { label: 'Imagem', value: 'IMAGE', icon: 'i-lucide-image' },
    { label: 'Vídeo', value: 'VIDEO', icon: 'i-lucide-video' },
    { label: 'Documento', value: 'DOCUMENT', icon: 'i-lucide-file-text' },
];

// Computed
const filteredTemplates = computed(() => {
    let result = Array.isArray(templates.value) ? templates.value : [];
    if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase();
        result = result.filter(t => t.name?.toLowerCase().includes(q));
    }
    if (filterCategory.value) result = result.filter(t => t.category === filterCategory.value);
    if (filterStatus.value) result = result.filter(t => t.status === filterStatus.value);
    return result;
});

const bodyCharCount = computed(() => (form.value.body || '').length);
const headerCharCount = computed(() => (form.value.header?.text || '').length);
const footerCharCount = computed(() => (form.value.footer || '').length);

const bodyVariables = computed(() => {
    const matches = (form.value.body || '').match(/\{\{\d+\}\}/g);
    return matches ? [...new Set(matches)] : [];
});

const nextVariableLabel = computed(() => {
    return '{{' + (bodyVariables.value.length + 1) + '}}';
});

const previewBody = computed(() => {
    let text = form.value.body || '';
    bodyVariables.value.forEach((v, i) => {
        const example = form.value.body_examples?.[i] || v;
        text = text.replace(v, example);
    });
    return text;
});

const currentCategory = computed(() => CATEGORIES.find(c => c.key === form.value.category) || CATEGORIES[0]);

function statusLabel(s) {
    return { APPROVED: 'Aprovado', PENDING: 'Pendente', REJECTED: 'Rejeitado', PAUSED: 'Pausado', DISABLED: 'Desativado' }[s] || s;
}
function statusColor(s) {
    return { APPROVED: 'bg-green-50 text-green-600 border-green-200', PENDING: 'bg-amber-50 text-amber-600 border-amber-200', REJECTED: 'bg-red-50 text-red-600 border-red-200', PAUSED: 'bg-orange-50 text-orange-600 border-orange-200', DISABLED: 'bg-gray-100 text-gray-500 border-gray-200' }[s] || 'bg-gray-100 text-gray-500 border-gray-200';
}
function categoryLabel(c) {
    return { MARKETING: 'Marketing', UTILITY: 'Utilidade', AUTHENTICATION: 'Autenticação' }[c] || c;
}
function formatDate(d) {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });
}

async function loadTemplates() {
    loading.value = true;
    try {
        const { data } = await WhatsappTemplatesAPI.index(props.inbox.id);
        const list = data.templates;
        templates.value = Array.isArray(list) ? list : [];
    } catch {
        templates.value = [];
        useAlert('Erro ao carregar templates.');
    }
    finally { loading.value = false; }
}

async function syncTemplates() {
    syncing.value = true;
    try {
        const { data } = await WhatsappTemplatesAPI.sync(props.inbox.id);
        const list = data.templates;
        templates.value = Array.isArray(list) ? list : [];
        useAlert('Templates sincronizados!');
    } catch { useAlert('Erro ao sincronizar.'); }
    finally { syncing.value = false; }
}

const deleting = ref(null);
async function deleteTemplate(tpl) {
    if (!confirm(`Tem certeza que deseja excluir o template "${tpl.name}"? Esta ação não pode ser desfeita.`)) return;
    deleting.value = tpl.name;
    try {
        await WhatsappTemplatesAPI.delete(props.inbox.id, tpl.id, tpl.name);
        useAlert('Template excluído com sucesso!');
        loadTemplates();
    } catch (e) {
        const status = e.response?.status;
        const msg = e.response?.data?.error;
        if (status === 500 || e.code === 'ECONNABORTED') {
            useAlert('A requisição demorou demais. Tente novamente.');
        } else {
            useAlert(msg || 'Erro ao excluir template.');
        }
    } finally {
        deleting.value = null;
    }
}

function openWizard() {
    form.value = { name: '', language: 'pt_BR', category: 'MARKETING', header: { type: '', text: '', example: '' }, body: '', body_examples: [], footer: '', buttons: [] };
    wizardStep.value = 1;
    showWizard.value = true;
}

const bodyTextarea = ref(null);

function insertVariable() {
    const nextNum = bodyVariables.value.length + 1;
    const variable = `{{${nextNum}}}`;
    const el = bodyTextarea.value;
    if (el) {
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const text = form.value.body;
        form.value.body = text.substring(0, start) + variable + text.substring(end);
        nextTick(() => {
            const newPos = start + variable.length;
            el.focus();
            el.setSelectionRange(newPos, newPos);
        });
    } else {
        form.value.body += variable;
    }
}

function addButton(type) {
    if (form.value.buttons.length >= 10) return;
    form.value.buttons.push({ type, text: '', url: '', phone_number: '', example: '' });
}
function removeButton(i) { form.value.buttons.splice(i, 1); }

function cleanButtons(buttons) {
    return buttons.map(btn => {
        const cleaned = { type: btn.type, text: btn.text };
        if (btn.type === 'URL' && btn.url) cleaned.url = btn.url;
        if (btn.type === 'PHONE_NUMBER' && btn.phone_number) cleaned.phone_number = btn.phone_number;
        if (btn.example) cleaned.example = btn.example;
        return cleaned;
    });
}

async function submitTemplate() {
    submitting.value = true;
    try {
        const sanitizedName = sanitizeTemplateName(form.value.name);
        if (!sanitizedName) {
            useAlert('Nome do template inválido. Use apenas letras minúsculas, números e underscores.');
            return;
        }
        const payload = { name: sanitizedName, language: form.value.language, category: form.value.category, body: form.value.body, footer: form.value.footer || undefined, body_examples: form.value.body_examples.length ? form.value.body_examples : undefined, buttons: form.value.buttons.length ? cleanButtons(form.value.buttons) : undefined };
        if (form.value.header.type === 'TEXT' && form.value.header.text) payload.header = { type: 'TEXT', text: form.value.header.text, example: form.value.header.example || undefined };
        else if (form.value.header.type && form.value.header.type !== 'TEXT') payload.header = { type: form.value.header.type };
        await WhatsappTemplatesAPI.create(props.inbox.id, payload);
        useAlert('Template enviado para processamento! A lista será atualizada em instantes.');
        showWizard.value = false;
        // Delay sync to give the background job time to finish
        setTimeout(() => { loadTemplates(); }, 5000);
    } catch (e) {
        const msg = e.response?.data?.error;
        useAlert(msg || 'Erro ao criar template.');
    }
    finally { submitting.value = false; }
}

onMounted(loadTemplates);
</script>

<template>
    <div class="mx-6 max-w-6xl pb-8">
        <div v-if="!showWizard">
            <div class="flex items-center justify-between mb-5">
                <div>
                    <h2 class="text-lg font-bold text-n-slate-12 flex items-center gap-2">
                        <span class="i-lucide-layout-template size-5 text-woot-500" />
                        Meus Templates
                    </h2>
                    <p class="text-xs text-n-slate-10 mt-0.5">Gerencie seus modelos de mensagem do WhatsApp Business
                    </p>
                </div>
                <div class="flex items-center gap-2">
                    <button
                        class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition"
                        :disabled="syncing" @click="syncTemplates">
                        <span class="i-lucide-refresh-cw size-3.5" :class="{ 'animate-spin': syncing }" />
                        Sincronizar
                    </button>
                    <button
                        class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 shadow-sm transition"
                        @click="openWizard">
                        <span class="i-lucide-plus size-3.5" /> Criar modelo
                    </button>
                </div>
            </div>

            <div class="flex items-center gap-2 mb-4">
                <div class="relative flex-1 max-w-xs">
                    <span class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10" />
                    <input v-model="searchQuery"
                        class="w-full pl-9 pr-3 py-2 rounded-lg border border-n-weak bg-n-background text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/20"
                        placeholder="Pesquisar template..." />
                </div>
                <FilterSelect v-model="filterCategory" :options="categoryFilterOptions" />
                <FilterSelect v-model="filterStatus" :options="statusFilterOptions" />
            </div>

            <div class="border border-n-weak rounded-xl overflow-hidden shadow-sm">
                <table class="w-full text-sm">
                    <thead class="bg-n-alpha-2 border-b border-n-weak">
                        <tr>
                            <th
                                class="text-left px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wider">
                                Nome</th>
                            <th
                                class="text-left px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wider">
                                Categoria</th>
                            <th
                                class="text-left px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wider">
                                Idioma</th>
                            <th
                                class="text-left px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wider">
                                Status</th>
                            <th
                                class="text-left px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wider">
                                Última edição</th>
                            <th
                                class="text-right px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wider">
                                Ações</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-n-weak">
                        <tr v-if="loading">
                            <td colspan="6" class="text-center py-16">
                                <span class="i-lucide-loader-2 size-6 animate-spin text-woot-500 inline-block" />
                            </td>
                        </tr>
                        <tr v-else-if="filteredTemplates.length === 0">
                            <td colspan="6" class="text-center py-16">
                                <span class="i-lucide-inbox size-10 text-n-slate-10 opacity-30 block mx-auto mb-2" />
                                <p class="text-xs text-n-slate-10">Nenhum template encontrado.</p>
                            </td>
                        </tr>
                        <tr v-for="tpl in filteredTemplates" :key="tpl.id || tpl.name"
                            class="hover:bg-n-alpha-1 transition group">
                            <td class="px-4 py-3.5">
                                <div class="font-medium text-n-slate-12">{{ tpl.name }}</div>
                                <div class="text-xs text-n-slate-10 mt-0.5 line-clamp-1 max-w-sm">
                                    {{tpl.components?.find(c => c.type === 'BODY')?.text || ''}}
                                </div>
                            </td>
                            <td class="px-4 py-3.5">
                                <span class="text-xs text-n-slate-11">{{ categoryLabel(tpl.category) }}</span>
                            </td>
                            <td class="px-4 py-3.5 text-xs text-n-slate-11">{{ tpl.language }}</td>
                            <td class="px-4 py-3.5">
                                <span class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border"
                                    :class="statusColor(tpl.status)">
                                    {{ statusLabel(tpl.status) }}
                                </span>
                            </td>
                            <td class="px-4 py-3.5 text-xs text-n-slate-10">{{ formatDate(tpl.updated_time) }}</td>
                            <td class="px-4 py-3.5 text-right">
                                <button
                                    class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition opacity-0 group-hover:opacity-100"
                                    :disabled="deleting === tpl.name"
                                    @click="deleteTemplate(tpl)">
                                    <span v-if="deleting === tpl.name" class="i-lucide-loader-2 size-3.5 animate-spin" />
                                    <span v-else class="i-lucide-trash-2 size-3.5" />
                                    Excluir
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p class="text-xs text-n-slate-10 mt-2 px-1">
                {{ filteredTemplates.length }} templates · {{(Array.isArray(templates) ? templates : []).filter(t =>
                    t.status === 'APPROVED').length}}
                ativos de 6.000
            </p>
        </div>

        <div v-else>
            <div class="flex items-center gap-0 mb-6 bg-n-alpha-1 rounded-xl p-1.5 border border-n-weak">
                <button v-for="(step, i) in [
                    { n: 1, icon: 'i-lucide-settings', label: 'Configurar modelo' },
                    { n: 2, icon: 'i-lucide-pencil', label: 'Editar modelo' },
                    { n: 3, icon: 'i-lucide-send', label: 'Enviar para análise' },
                ]" :key="step.n"
                    class="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium rounded-lg px-3 py-2 transition"
                    :class="wizardStep === step.n ? 'bg-woot-500 text-white shadow-sm' : wizardStep > step.n ? 'text-woot-600 bg-woot-50' : 'text-n-slate-10'"
                    :disabled="step.n > wizardStep" @click="wizardStep = Math.min(step.n, wizardStep)">
                    <span :class="step.icon" class="size-3.5" />
                    {{ step.label }}
                </button>
            </div>

            <div class="flex gap-6">
                <div class="flex-1 min-w-0">
                    <div v-if="wizardStep === 1">
                        <div class="rounded-xl border border-n-weak bg-n-background p-6 shadow-sm">
                            <h3 class="text-base font-bold text-n-slate-12 mb-1">Configurar seu modelo</h3>
                            <p class="text-sm text-n-slate-10 mb-5">Escolha a categoria que melhor descreve o seu modelo
                                de mensagem.</p>

                            <div class="flex gap-3 mb-6">
                                <button v-for="cat in CATEGORIES" :key="cat.key"
                                    class="flex-1 p-4 rounded-xl border-2 transition text-left"
                                    :class="form.category === cat.key ? 'border-woot-500 bg-woot-25 shadow-sm' : 'border-n-weak hover:border-n-strong hover:bg-n-alpha-1'"
                                    @click="form.category = cat.key">
                                    <div class="flex items-center gap-2 mb-2">
                                        <span :class="[cat.icon, cat.color, cat.bg]"
                                            class="size-8 rounded-lg flex items-center justify-center p-1.5" />
                                        <span class="text-sm font-semibold text-n-slate-12">{{ cat.label }}</span>
                                    </div>
                                    <p class="text-xs text-n-slate-10 leading-relaxed">{{ cat.desc }}</p>
                                </button>
                            </div>

                            <div class="p-4 rounded-xl bg-n-alpha-1 border border-n-weak mb-6">
                                <div class="flex items-center gap-2">
                                    <span class="w-2.5 h-2.5 rounded-full bg-woot-500 ring-4 ring-woot-500/20"></span>
                                    <span class="text-sm font-semibold text-n-slate-12">Padrão</span>
                                </div>
                                <p class="text-xs text-n-slate-10 mt-1 ml-[18px]">
                                    Envie mensagens com mídia e botões personalizados.
                                </p>
                            </div>
                        </div>

                        <div class="flex justify-between mt-4">
                            <button class="px-4 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2 rounded-lg transition"
                                @click="showWizard = false">Descartar</button>
                            <button
                                class="px-5 py-2 text-sm font-semibold text-white bg-woot-500 hover:bg-woot-600 rounded-lg shadow-sm transition"
                                @click="wizardStep = 2">Avançar</button>
                        </div>
                    </div>

                    <div v-if="wizardStep === 2" class="flex flex-col gap-5">
                        <div class="flex items-center gap-2">
                            <span :class="[currentCategory.icon, currentCategory.color, currentCategory.bg]"
                                class="size-7 rounded-lg flex items-center justify-center p-1" />
                            <span class="text-sm font-semibold text-n-slate-12">{{ currentCategory.label }}</span>
                            <span class="text-xs text-n-slate-10">· Padrão</span>
                        </div>

                        <div class="rounded-xl border border-n-weak bg-n-background p-5 shadow-sm">
                            <h4 class="text-sm font-bold text-n-slate-12 mb-3 flex items-center gap-1.5">
                                <span class="i-lucide-tag size-4 text-n-slate-10" /> Nome e idioma
                            </h4>
                            <div class="grid grid-cols-2 gap-4">
                                <div>
                                    <label class="text-xs font-medium text-n-slate-10 mb-1.5 block">Nome do
                                        modelo</label>
                                    <input v-model="form.name"
                                        class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
                                        placeholder="meu_template_nome" />
                                    <p class="text-xs text-n-slate-10 mt-1">Apenas letras minúsculas e underscores.</p>
                                </div>
                                <div>
                                    <label class="text-xs font-medium text-n-slate-10 mb-1.5 block">Idioma</label>
                                    <FilterSelect v-model="form.language" :options="languageOptions" />
                                </div>
                            </div>
                        </div>

                        <div class="rounded-xl border border-n-weak bg-n-background p-5 shadow-sm">
                            <h4 class="text-sm font-bold text-n-slate-12 mb-3 flex items-center gap-1.5">
                                <span class="i-lucide-file-text size-4 text-n-slate-10" /> Conteúdo
                            </h4>

                            <div class="mb-4">
                                <label class="text-xs font-medium text-n-slate-10 mb-1.5 block">Amostra de mídia ·
                                    <span class="text-n-slate-9">Opcional</span></label>
                                <FilterSelect v-model="form.header.type" :options="mediaTypeOptions" />
                            </div>

                            <div v-if="form.header.type === 'TEXT'" class="mb-4">
                                <label class="text-xs font-medium text-n-slate-10 mb-1.5 block">Cabeçalho</label>
                                <div class="relative">
                                    <input v-model="form.header.text"
                                        class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/20"
                                        placeholder="Texto do cabeçalho" maxlength="60" />
                                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-n-slate-9">{{
                                        headerCharCount }}/60</span>
                                </div>
                            </div>

                            <div class="mb-4">
                                <label class="text-xs font-medium text-n-slate-10 mb-1.5 block">Corpo</label>
                                <textarea ref="bodyTextarea" v-model="form.body" rows="5"
                                    class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/20 resize-y"
                                    placeholder="Olá {{1}}, sua compra {{2}} foi confirmada!" />
                                <div class="flex items-center justify-between mt-1">
                                    <div class="flex items-center gap-2">
                                        <button type="button"
                                            class="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md bg-woot-50 text-woot-600 hover:bg-woot-100 dark:bg-woot-900/30 dark:text-woot-400 dark:hover:bg-woot-900/50 border border-woot-200 dark:border-woot-800 transition"
                                            @click="insertVariable">
                                            <span class="i-lucide-braces size-3" />
                                            Inserir variável
                                        </button>
                                        <p class="text-xs text-n-slate-9">
                                            Próxima: <code class="bg-n-alpha-2 px-1 rounded text-[10px]">{{ nextVariableLabel }}</code>
                                        </p>
                                    </div>
                                    <span class="text-xs text-n-slate-9">{{ bodyCharCount }}/1024</span>
                                </div>
                            </div>

                            <div v-if="bodyVariables.length > 0"
                                class="mb-4 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
                                <label class="text-xs font-medium text-n-slate-10 mb-2 block">Exemplos de
                                    variáveis</label>
                                <div v-for="(v, i) in bodyVariables" :key="v" class="flex items-center gap-2 mb-1.5">
                                    <code
                                        class="text-xs font-mono text-woot-600 bg-woot-50 px-1.5 py-0.5 rounded w-14 text-center">{{ v }}</code>
                                    <input v-model="form.body_examples[i]"
                                        class="flex-1 rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-woot-500/20"
                                        placeholder="Valor de exemplo..." />
                                </div>
                            </div>

                            <div>
                                <label class="text-xs font-medium text-n-slate-10 mb-1.5 block">Rodapé ·
                                    <span class="text-n-slate-9">Opcional</span></label>
                                <div class="relative">
                                    <input v-model="form.footer"
                                        class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-woot-500/20"
                                        placeholder="Texto do rodapé" maxlength="60" />
                                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-n-slate-9">{{
                                        footerCharCount }}/60</span>
                                </div>
                            </div>
                        </div>

                        <div class="rounded-xl border border-n-weak bg-n-background p-5 shadow-sm">
                            <h4 class="text-sm font-bold text-n-slate-12 mb-1 flex items-center gap-1.5">
                                <span class="i-lucide-mouse-pointer-click size-4 text-n-slate-10" /> Botões
                                <span class="text-xs font-normal text-n-slate-9">· Opcional</span>
                            </h4>
                            <p class="text-xs text-n-slate-10 mb-3">Máx 10 botões. Mais de 3 aparecem em menu.</p>

                            <div v-for="(btn, i) in form.buttons" :key="i"
                                class="flex items-start gap-3 mb-2 p-3 rounded-lg bg-n-alpha-1 border border-n-weak group">
                                <div class="flex items-center gap-1.5 mt-1 shrink-0">
                                    <span :class="BUTTON_TYPES.find(b => b.key === btn.type)?.icon || 'i-lucide-circle'"
                                        class="size-3.5 text-n-slate-10" />
                                    <span class="text-xs font-medium text-n-slate-10 w-20">{{BUTTON_TYPES.find(b =>
                                        b.key === btn.type)?.label || btn.type}}</span>
                                </div>
                                <div class="flex-1 flex flex-col gap-1.5">
                                    <input v-model="btn.text"
                                        class="w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-woot-500/20"
                                        placeholder="Texto do botão" />
                                    <input v-if="btn.type === 'URL'" v-model="btn.url"
                                        class="w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-woot-500/20"
                                        placeholder="https://..." />
                                    <input v-if="btn.type === 'PHONE_NUMBER'" v-model="btn.phone_number"
                                        class="w-full rounded-lg border border-n-weak bg-n-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-woot-500/20"
                                        placeholder="+55 11 99999-9999" />
                                </div>
                                <button
                                    class="text-n-slate-10 hover:text-red-500 mt-1.5 opacity-0 group-hover:opacity-100 transition"
                                    @click="removeButton(i)">
                                    <span class="i-lucide-trash-2 size-3.5" />
                                </button>
                            </div>

                            <div v-if="form.buttons.length < 10" class="relative inline-block">
                                <details class="group">
                                    <summary
                                        class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-dashed border-n-weak text-n-slate-11 hover:bg-n-alpha-1 cursor-pointer select-none transition">
                                        <span class="i-lucide-plus size-3" /> Adicionar botão
                                        <span class="i-lucide-chevron-down size-3" />
                                    </summary>
                                    <div
                                        class="absolute z-10 mt-1 w-56 rounded-xl border border-n-strong bg-n-solid-1 shadow-xl p-1">
                                        <button v-for="bt in BUTTON_TYPES" :key="bt.key"
                                            class="flex items-center gap-2 w-full px-3 py-2 text-xs text-n-slate-12 rounded-lg hover:bg-n-alpha-2 transition"
                                            @click="addButton(bt.key); $event.target.closest('details').open = false">
                                            <span :class="bt.icon" class="size-3.5 text-n-slate-10" /> {{ bt.label }}
                                        </button>
                                    </div>
                                </details>
                            </div>
                        </div>

                        <div class="flex justify-between">
                            <button class="px-4 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2 rounded-lg transition"
                                @click="wizardStep = 1">Voltar</button>
                            <button
                                class="px-5 py-2 text-sm font-semibold text-white bg-woot-500 hover:bg-woot-600 rounded-lg shadow-sm transition"
                                :disabled="!form.name || !form.body" @click="wizardStep = 3">
                                Enviar para análise
                            </button>
                        </div>
                    </div>

                    <div v-if="wizardStep === 3">
                        <div class="rounded-xl border border-n-weak bg-n-background p-6 shadow-sm mb-4">
                            <h3 class="text-base font-bold text-n-slate-12 mb-4 flex items-center gap-1.5">
                                <span class="i-lucide-check-circle size-5 text-green-500" />
                                Revisar e enviar
                            </h3>

                            <div class="grid grid-cols-3 gap-4 mb-5">
                                <div class="p-3 rounded-lg bg-n-alpha-1">
                                    <p class="text-xs text-n-slate-9 mb-0.5">Nome</p>
                                    <p class="text-sm font-semibold text-n-slate-12">{{ form.name }}</p>
                                </div>
                                <div class="p-3 rounded-lg bg-n-alpha-1">
                                    <p class="text-xs text-n-slate-9 mb-0.5">Categoria</p>
                                    <p class="text-sm font-semibold text-n-slate-12">{{ categoryLabel(form.category) }}
                                    </p>
                                </div>
                                <div class="p-3 rounded-lg bg-n-alpha-1">
                                    <p class="text-xs text-n-slate-9 mb-0.5">Idioma</p>
                                    <p class="text-sm font-semibold text-n-slate-12">{{ form.language }}</p>
                                </div>
                            </div>

                            <div v-if="form.header.type === 'TEXT' && form.header.text" class="mb-3">
                                <p class="text-xs text-n-slate-9">Cabeçalho</p>
                                <p class="text-sm text-n-slate-12 font-medium">{{ form.header.text }}</p>
                            </div>
                            <div class="mb-3">
                                <p class="text-xs text-n-slate-9">Corpo</p>
                                <p
                                    class="text-sm text-n-slate-12 whitespace-pre-wrap mt-0.5 p-3 bg-n-alpha-1 rounded-lg">
                                    {{ form.body }}</p>
                            </div>
                            <div v-if="form.footer" class="mb-3">
                                <p class="text-xs text-n-slate-9">Rodapé</p>
                                <p class="text-sm text-n-slate-11">{{ form.footer }}</p>
                            </div>
                            <div v-if="form.buttons.length">
                                <p class="text-xs text-n-slate-9 mb-1">Botões</p>
                                <div class="flex flex-wrap gap-1.5">
                                    <span v-for="(btn, i) in form.buttons" :key="i"
                                        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-n-alpha-1 border border-n-weak text-xs text-n-slate-11">
                                        {{ btn.text || 'Botão' }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="flex justify-between">
                            <button class="px-4 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-2 rounded-lg transition"
                                @click="wizardStep = 2">Voltar</button>
                            <button
                                class="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg shadow-sm transition"
                                :disabled="submitting" @click="submitTemplate">
                                <span v-if="submitting" class="i-lucide-loader-2 size-3.5 animate-spin" />
                                <span v-else class="i-lucide-send size-3.5" />
                                Enviar para análise
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="wizardStep >= 2" class="w-[280px] shrink-0 sticky top-4 self-start">
                    <h4 class="text-xs font-bold text-n-slate-12 mb-3 uppercase tracking-wider">Prévia do modelo</h4>

                    <div class="rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                        <div class="bg-[#075e54] px-3 py-2.5 flex items-center gap-2">
                            <span class="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                <span class="i-lucide-user size-4 text-white/70" />
                            </span>
                            <div>
                                <p class="text-white text-xs font-semibold">Contato</p>
                                <p class="text-white/60 text-[10px]">online</p>
                            </div>
                        </div>

                        <div class="bg-[#ece5dd] p-3 min-h-[280px] flex flex-col justify-end"
                            style="background-image: url('data:image/svg+xml,%3Csvg width=\'200\' height=\'200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'p\' width=\'40\' height=\'40\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M20 0L40 20L20 40L0 20Z\' fill=\'none\' stroke=\'%23d4cfc4\' stroke-width=\'.5\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'200\' height=\'200\' fill=\'url(%23p)\'/%3E%3C/svg%3E')">
                            <div class="bg-white rounded-lg rounded-tl-none shadow-sm p-2.5 max-w-[240px]">
                                <div v-if="form.header.type === 'IMAGE'"
                                    class="bg-gray-100 rounded-lg h-28 flex items-center justify-center mb-2">
                                    <span class="i-lucide-image size-8 text-gray-300" />
                                </div>
                                <div v-if="form.header.type === 'VIDEO'"
                                    class="bg-gray-800 rounded-lg h-28 flex items-center justify-center mb-2">
                                    <span class="i-lucide-play size-10 text-white/80" />
                                </div>
                                <div v-if="form.header.type === 'DOCUMENT'"
                                    class="bg-gray-100 rounded-lg h-14 flex items-center gap-2 px-3 mb-2">
                                    <span class="i-lucide-file-text size-6 text-red-400" />
                                    <span class="text-[10px] text-gray-500">documento.pdf</span>
                                </div>
                                <div v-if="form.header.type === 'TEXT' && form.header.text"
                                    class="font-bold text-[13px] text-gray-900 mb-1">
                                    {{ form.header.text }}
                                </div>

                                <p class="text-[13px] text-gray-800 whitespace-pre-wrap leading-[1.4]">{{ previewBody ||
                                    'Corpo da mensagem' }}</p>

                                <p v-if="form.footer" class="text-[11px] text-gray-400 mt-1.5">{{ form.footer }}</p>

                                <p class="text-[10px] text-gray-400 text-right mt-1">{{ new
                                    Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}</p>
                            </div>

                            <div v-if="form.buttons.length" class="flex flex-col gap-0.5 mt-0.5 max-w-[240px]">
                                <button v-for="(btn, i) in form.buttons" :key="i"
                                    class="w-full py-1.5 text-[12px] font-medium text-[#00a5f4] bg-white rounded-lg shadow-sm flex items-center justify-center gap-1">
                                    <span v-if="btn.type === 'URL'" class="i-lucide-external-link size-3" />
                                    <span v-if="btn.type === 'PHONE_NUMBER'" class="i-lucide-phone size-3" />
                                    <span v-if="btn.type === 'COPY_CODE'" class="i-lucide-copy size-3" />
                                    <span v-if="btn.type === 'QUICK_REPLY'" class="i-lucide-reply size-3" />
                                    {{ btn.text || 'Botão' }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="mt-3 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
                        <p class="text-xs font-semibold text-n-slate-12 mb-1">Este modelo é ideal para</p>
                        <p class="text-[11px] text-n-slate-10 leading-relaxed">{{ currentCategory.desc }}</p>
                        <p class="text-xs font-semibold text-n-slate-12 mt-2 mb-1">Áreas personalizáveis</p>
                        <p class="text-[11px] text-n-slate-10">Mídia, cabeçalho, corpo, rodapé e botão</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
